import React, { useState, useEffect } from "react"
import { useQuery, useQueryClient } from "react-query"
import { fetchOpenPositions, fetchAllOpenPositionsForCSV } from "../../services/main.service"
import Paginator from "../composites/paginator"
import WithAuth from "../hoc/with-auth"
import { LayoutWithSidebar } from "../layouts/layout-with-sidebar.tsx"
import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Grid,
  GridItem,
  Button,
  Input,
  IconButton,
  useToast,
} from "@chakra-ui/react"
import _ from "lodash"
import BlockedLoader from "../composites/blocked-loader"
import DateRangePicker from "../composites/date-range-picker"
import { AiOutlineSearch } from "react-icons/ai"
import { InputWrapper } from "../composites"
import { ReactSelect } from "../composites/select"
import { SelectSymbols } from "../composites/select-symbols"
import { AiOutlineDownload } from "react-icons/ai"
import { getBaseURL } from "../../services/api"
import { formatDate } from "../../utils"

// Import utility functions for CSV conversion
const convertJsonToCsv = (jsonArray: Record<string, any>[]) => {
  if (!jsonArray || jsonArray.length === 0) {
    return '';
  }
  
  // Extract headers from the first object in the array
  const headers = Object.keys(jsonArray[0]);
  
  // Create the CSV header row
  const csvHeader = headers.join(',');
  
  // Create the CSV data rows with optimized processing
  const csvRows = jsonArray.map(obj => {
    return headers
      .map(header => {
        const value = obj[header];
        
        // Handle null/undefined values
        if (value === null || value === undefined) {
          return '';
        }
        
        // Special handling for the Time column to ensure proper Excel formatting
        if (header === 'Time' && value) {
          return `"${value}"`;
        }
        
        // If the value contains commas, quotes, or newlines, wrap it in double quotes
        // and escape any existing quotes
        if (typeof value === 'string' && 
            (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes(' '))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        
        return value;
      })
      .join(',');
  });
  
  // Combine the header and rows to form the complete CSV string
  return [csvHeader, ...csvRows].join('\n');
}

interface DownloadFileParams {
  data: string;
  fileName: string;
  fileType: string;
}

/**
 * Creates and triggers download of a file
 */
const downloadFile = ({ data, fileName, fileType }: DownloadFileParams) => {
  // Create blob with the data
  const blob = new Blob([data], { type: fileType });
  
  // Create download link
  const a = document.createElement('a');
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  
  // Trigger download and clean up
  document.body.appendChild(a); // Needed for Firefox
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(a.href); // Free memory
}

/**
 * Transforms API data for Open Positions CSV download with better performance
 * @param apiResponse Direct API response data
 * @param columns Array of column headers to include
 * @param fileName Base name for the downloaded file
 */
const downloadOpenPositionsToCSV = (apiResponse: any, columns: string[], fileName: string) => {
  // Check the actual structure of the API response
  if (!apiResponse?.data?.deals || apiResponse.data.deals.length === 0) {
    console.warn("No data available for download");
    return;
  }
  
  // Process in batches to prevent UI freeze
  const batchSize = 500;
  const deals = apiResponse.data.deals;
  const totalDeals = deals.length;
  let processedData: Record<string, any>[] = [];
  
  // Map API field keys to readable column names
  const fieldToColumnMap: Record<string, string> = {
    'd': 'Deal',
    'pid': 'Position',
    'l': 'Login',
    't': 'Time',
    'TimeCreate': 'Time', // Adding this mapping for the TimeCreate field
    'e': 'Entry',
    's': 'Symbol',
    'v': 'Volume',
    'p': 'Price',
    'sl': 'S/L',
    'tp': 'T/P',
    'r': 'Reason',
    'pr': 'Profit',
    'de': 'Dealer',
    'co': 'Comment'
  };
  
  // Process data in batches using setTimeout to keep UI responsive
  const processBatch = (startIndex: number) => {
    const endIndex = Math.min(startIndex + batchSize, totalDeals);
    
    for (let i = startIndex; i < endIndex; i++) {
      const deal = deals[i];
      const formattedDeal: Record<string, any> = {};
      
      // Format each column according to the display requirements
      columns.forEach(column => {
        if (column === 'Name') {
          // Special handling for Name which combines first, last and middle names
          formattedDeal[column] = `${deal.firstn || ''} ${deal.lastn || ''} ${deal.middlen || ''}`.trim();
        } else {
          // Map API field to column name using our lookup table
          const apiField = Object.keys(fieldToColumnMap).find(
            key => fieldToColumnMap[key] === column
          );
          
          if (apiField && deal[apiField] !== undefined) {
            formattedDeal[column] = deal[apiField];
          } else if (column === 'Time' && deal['TimeCreate'] !== undefined) {
            // Special case for Time field which might be in TimeCreate
            formattedDeal[column] = deal['TimeCreate'];
          } else {
            formattedDeal[column] = '';
          }
        }
      });
      
      processedData.push(formattedDeal);
    }
    
    // If there's more data to process, schedule next batch
    if (endIndex < totalDeals) {
      setTimeout(() => processBatch(endIndex), 0);
    } else {
      // All data processed, generate and download CSV
      const csvData = convertJsonToCsv(processedData);
      downloadFile({
        data: csvData,
        fileName: `${fileName}.csv`,
        fileType: 'text/csv'
      });
    }
  };
  
  // Start processing the first batch
  processBatch(0);
}

const captions = [
  "Deal",
  "Position",
  "Login",
  "Name",
  "Time",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S/L",
  "T/P",
  "Reason",
  "Profit",
  "Dealer",
  "Comment",
]

// Define the type for the deal array items
type PositionItemArray = string | number | null;

// Define type for position object
interface PositionItem {
  d: string;       // Deal
  pid: string;     // Position
  l: string;       // Login
  t: string;       // Time
  e: string;       // Entry
  s: string;       // Symbol
  v: string;       // Volume
  p: string;       // Price
  sl: string;      // S/L
  tp: string;      // T/P
  r: string;       // Reason
  pr: string;      // Profit
  de: string;      // Dealer
  co: string;      // Comment
  firstn: string;  // First Name
  lastn: string;   // Last Name
  middlen: string; // Middle Name
}

const OpenPositionsPage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToast()

  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined])

  const [loginId, setLoginId] = useState("")
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [finalState, setFinalState] = useState<{
    year: string
    loginId: string
    symbolStr: string
    startDate: Date | undefined
    endDate: Date | undefined
  }>({
    year: year,
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
  })

  const updateFinalState = () => {
    setFinalState({
      year: year,
      loginId,
      symbolStr,
      startDate,
      endDate,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
    refetch,
  } = useQuery(
    [
      "open-positions",
      {
        currentPage,
        finalState,
      },
    ],
    () =>
      fetchOpenPositions(
        finalState.year,
        currentPage,
        finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.endDate
          ? formatDate(finalState?.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr
      ),
    {
      refetchInterval: 15000,
    }
  )

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage, year])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

  // Optimized download function that fetches all data at once
  const downloadCSV = async () => {
    try {
      setIsDownloading(true);
      
      // Fetch all data for CSV with the current filter parameters
      const response = await fetchAllOpenPositionsForCSV(
        finalState.year,
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr
      );
      
      // Check if we have deals data - using the correct property name from the API
      if (!response?.data?.deals || response.data.deals.length === 0) {
        toast({
          title: "No data available",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        setIsDownloading(false);
        return;
      }
      
      // Use our optimized function to download directly from API response
      downloadOpenPositionsToCSV(
        response,
        captions,
        `open-positions-${finalState.year}-${formatDate(finalState.startDate || new Date())}`
      );
      
      toast({
        title: "Download successful",
        description: `${response.data.deals.length} records downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast({
        title: "Download failed",
        description: "There was an error downloading the CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
        display="none"
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Enter Login ID">
            <Input
              value={loginId}
              onChange={(e) => setLoginId(e?.target?.value)}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <SelectSymbols
            selectedSymbol={symbolStr ?? "ALL"}
            onOptionChange={(_option: string) => {
              setSymbolStr(_option)
            }}
          />
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  updateFinalState()
                }}
              >
                <Box as={AiOutlineSearch} mr={2} />
                Search
              </Button>
            </Box>
          </Flex>
        </Box>
      </Grid>

      <Box textAlign="right" my={4}>
        <Button 
          colorScheme="green" 
          leftIcon={<AiOutlineDownload />}
          isDisabled={isLoading || isDownloading}
          isLoading={isDownloading}
          loadingText="Downloading..."
          onClick={downloadCSV}
        >
          Download
        </Button>
      </Box>

      <DealsGrid>
        {isLoading ? (
          <Tr>
            <Td colSpan={captions?.length}>
              <BlockedLoader isBlocked={isLoading}>
                <></>
              </BlockedLoader>
            </Td>
          </Tr>
        ) : (
          <>
            {data?.deals?.data?.length ? (
              <>
                {_.map(data?.deals?.data ?? [], (deal) => {
                  return <DealsTableRow key={deal} deal={deal} />
                })}
              </>
            ) : (
              <>
                <Tr>
                  <Td colSpan={captions?.length}>
                    <Box p={4} textAlign="center">
                      <Text color="text.regular">
                        No data found for the selected dates between
                      </Text>
                    </Box>
                  </Td>
                </Tr>
              </>
            )}
          </>
        )}
      </DealsGrid>

      <Box my={4}>
        <Paginator
          currentPage={currentPage}
          totalPages={data?.deals?.last_page ?? 1}
          onChangePage={(_p: number) => setCurrentpage(_p)}
        />
      </Box>
    </>
  )
}

export default OpenPositionsPage

const DealsGrid = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Box overflowX="auto">
        <Table variant="striped" color="text.regular" border={1}>
          <Thead>
            <Tr my=".8rem" ps="0px">
              {captions.map((caption, idx) => {
                return (
                  <Th
                    color="gray.400"
                    key={idx}
                    ps={idx === 0 ? "0px" : "0px"}
                    textAlign={caption === "PL" ? "right" : "unset"}
                  >
                    {caption}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </>
  )
}

const DealsTableRow = ({ deal }: { deal: any }) => {
  return (
    <>
      <Tr>
        {_.map(deal, (_d, index) => {
          if (deal?.length === index + 1) return <></>

          return (
            <Td pl={1} pr={2} key={index}>
              <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                <Text fontSize="md" fontWeight="bold" minWidth="100%">
                  {_d}
                </Text>
              </Flex>
            </Td>
          )
        })}
      </Tr>
    </>
  )
}
